import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Table from "../../../components/Table";
import { Dropdown } from "react-bootstrap";
import { usePersistStore, useStore } from "../../../store";
import { AdService, SettingService, TrustapUserService } from "../../../services";
import { useMyListings } from "../../../hooks/queries";
import { AD_STATUS } from "../../../utils/enums";
import { Utils } from "../../../utils";
import AdStatusDropdown from "../../../components/AdStatusDropdown";
import AdSortingDropdown from "../../../components/AdSortingDropdown";
import Confirm from "../../../components/Confirm";
import AdDetailsPopup from "../../../components/AdDetailsPopup";
import CommonModal from "../../../components/CommonModal";
import GetVerifiedFirst from "../../../components/GetVerifiedFirst";
import TableSearchInput from "../../../components/TableSearchInput";
import { AdStatusChip } from "../../../components/status";

function List() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("");
  const [sorting, setSorting] = useState("");
  const [keyword, setKeyword] = useState("");
  // Search applies on Enter (not per keystroke), preserving prior behavior.
  const [appliedKeyword, setAppliedKeyword] = useState("");
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const { token, isVerifiedUser, user } = usePersistStore((s: any) => s);
  const { setIsLoading, setErrorMessage } = useStore((s: any) => s);

  const { data, isLoading, error, refetch } = useMyListings(token, {
    keyword: appliedKeyword,
    page,
    limit: rowsPerPage,
    status,
    sorting,
  });
  const list = data?.ads || [];
  const count = data?.count || 0;
  const isDone = !isLoading;

  useEffect(() => {
    if (error) setErrorMessage((error as Error).message);
  }, [error, setErrorMessage]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
  const [showDetailsPopup, setShowDetailsPopup] = useState(false);
  const [showGetVerifiedFirst, setShowGetVerifiedFirst] = useState(false);
  const [gateReason, setGateReason] = useState<
    "not-verified" | "incomplete-profile" | "no-full-trustap"
  >("not-verified");

  const handleCreateClick = async () => {
    let hasFullTrustapAccount = false;
    if (token) {
      const result = await TrustapUserService.getMyTrustapAccount(token);
      hasFullTrustapAccount = result?.data === true;
    }
    const gate = Utils.canCreateListing(
      user,
      isVerifiedUser,
      hasFullTrustapAccount
    );
    if (gate.ok) {
      navigate("/dashboard/create");
      return;
    }
    setGateReason(gate.reason);
    setShowGetVerifiedFirst(true);
  };
  const [selectedObj, setSelectedObj] = useState<any>(null);
  const [platformCommission, setPlatformCommission] = useState<number>(0);

  useEffect(() => {
    const fetchSettings = async () => {
      const result = await SettingService.get(token);
      if (result?.data?.platformCommission != null) {
        setPlatformCommission(result.data.platformCommission);
      }
    };
    fetchSettings();
  }, [token]);

  const renderPercentageChange = useCallback((row: any) => {
    if (row?.status !== AD_STATUS.SOLD) return "";
    return Utils.formatPercentChange(row?.price, row?.priceSold, 2);
  }, []);

  const renderCommission = useCallback(
    (row: any) => {
      if (row?.status !== AD_STATUS.SOLD) return "";
      const soldPrice = parseFloat(row?.priceSold);
      if (Number.isNaN(soldPrice)) return "";
      const value = soldPrice * (platformCommission / 100);
      return `${platformCommission}% (${Utils.formatToCurrency(value)})`;
    },
    [platformCommission]
  );

  const renderBadge = useCallback(
    (row: any) => (
      <AdStatusChip status={row?.status} biddingEnds={row?.biddingEnds} />
    ),
    []
  );

  const renderActions = useCallback(
    (row: any) => {
      // Editing is allowed only while the listing is still under-review —
      // once approved, live, sold, or closed, the auction details are
      // locked. Deletion is blocked on live (on-sale) and sold listings:
      // pulling a live auction would strand active bids, and sold ones
      // are tied to a completed transaction.
      const canEdit = row?.status === AD_STATUS.UNDER_REVIEW;
      const canDelete =
        row?.status !== AD_STATUS.ON_SALE && row?.status !== AD_STATUS.SOLD;
      if (!canEdit && !canDelete) return null;
      return (
        <div className="profileDropdown">
          <Dropdown>
            <Dropdown.Toggle aria-label="Row actions">
              <i className="bi bi-three-dots-vertical" aria-hidden="true" />
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {canEdit && (
                <Dropdown.Item
                  onClick={() => navigate(`/dashboard/update/${row?._id}`)}
                >
                  <div className="d-flex gap-2 align-items-center">
                    <i className="bi bi-pencil-square" aria-hidden="true" />
                    <span>Edit</span>
                  </div>
                </Dropdown.Item>
              )}
              {canDelete && (
                <Dropdown.Item
                  onClick={() => {
                    setSelectedObj(row);
                    setShowDeleteConfirm(true);
                  }}
                >
                  <div className="d-flex gap-2 align-items-center">
                    <i className="bi bi-trash" aria-hidden="true" />
                    <span>Delete</span>
                  </div>
                </Dropdown.Item>
              )}
            </Dropdown.Menu>
          </Dropdown>
        </div>
      );
    },
    [navigate]
  );

  const renderMobileCard = useCallback(
    (row: any) => (
      <div className="bidsCard">
        <div className="bidsCard__top">
          <div>
            <Link
              to={`/details/${row?._id}`}
              className="bidsCard__title"
            >
              {row?.title}
            </Link>
            {row?.code && (
              <div className="refCode refCode--sub">{row.code}</div>
            )}
          </div>
          <div className="bidsCard__status">{renderBadge(row)}</div>
        </div>
        <div className="bidsCard__grid">
          <div className="bidsCard__stat">
            <span className="bidsCard__statLabel">Opening</span>
            <span className="bidsCard__statValue">
              {Utils.formatToCurrency(row?.price)}
            </span>
          </div>
          {row?.status === AD_STATUS.SOLD ? (
            <>
              <div className="bidsCard__stat">
                <span className="bidsCard__statLabel">Sold</span>
                <span className="bidsCard__statValue">
                  {Utils.formatToCurrency(row?.priceSold)}
                </span>
              </div>
              <div className="bidsCard__stat">
                <span className="bidsCard__statLabel">Change</span>
                <span className="bidsCard__statValue">
                  {renderPercentageChange(row)}
                </span>
              </div>
            </>
          ) : (
            <div className="bidsCard__stat bidsCard__stat--span2">
              <span className="bidsCard__statLabel">Status</span>
              <span className="bidsCard__statValue">
                {renderBadge(row)}
              </span>
            </div>
          )}
        </div>
        <div className="bidsCard__meta">
          <div className="bidsCard__metaRow">
            <i className="bi bi-calendar-event" aria-hidden="true"></i>
            <span>Starts {Utils.formatDateTime(row?.biddingStarts)}</span>
          </div>
          <div className="bidsCard__metaRow">
            <i className="bi bi-hourglass-split" aria-hidden="true"></i>
            <span>Closes {Utils.formatDateTime(row?.biddingEnds)}</span>
          </div>
          {row?.status === AD_STATUS.SOLD && renderCommission(row) && (
            <div className="bidsCard__metaRow">
              <i className="bi bi-percent" aria-hidden="true"></i>
              <span>Commission {renderCommission(row)}</span>
            </div>
          )}
        </div>
        {renderActions(row) && (
          <div className="bidsCard__actions">{renderActions(row)}</div>
        )}
      </div>
    ),
    [renderBadge, renderPercentageChange, renderCommission, renderActions]
  );

  const columns = useMemo(
    () => [
      {
        title: "Code",
        thAlign: "left",
        tdAlign: "left",
        formatter: (row: any) =>
          row?.code ? (
            <span className="refCode">{row.code}</span>
          ) : (
            <span style={{ color: "var(--text-muted)" }}>—</span>
          ),
      },
      {
        title: "Title",
        thAlign: "left",
        tdAlign: "left",
        formatter: (row: any) => (
          <Link to={`/details/${row?._id}`} style={{ color: "var(--main-color)", textDecoration: "none" }}>
            {row?.title}
          </Link>
        ),
      },
      {
        title: "Opening Price",
        thAlign: "right",
        tdAlign: "right",
        formatter: (row: any) => Utils.formatToCurrency(row?.price),
      },
      {
        title: "Selling Price",
        thAlign: "right",
        tdAlign: "right",
        formatter: (row: any) =>
          row?.status !== AD_STATUS.SOLD
            ? ""
            : Utils.formatToCurrency(row?.priceSold),
      },
      {
        title: "% Change",
        thAlign: "right",
        tdAlign: "right",
        formatter: (row: any) => renderPercentageChange(row),
      },
      {
        title: "Platform Commission",
        thAlign: "right",
        tdAlign: "right",
        formatter: (row: any) => renderCommission(row),
      },
      {
        title: "Bidding Starts",
        thAlign: "center",
        tdAlign: "center",
        formatter: (row: any) => Utils.formatDateTime(row?.biddingStarts),
      },
      {
        title: "Closes at",
        thAlign: "center",
        tdAlign: "center",
        formatter: (row: any) => Utils.formatDateTime(row?.biddingEnds),
      },
      {
        title: "Status",
        thAlign: "center",
        tdAlign: "center",
        formatter: (row: any) => renderBadge(row),
      },
      {
        title: "Actions",
        thAlign: "center",
        tdAlign: "center",
        formatter: (row: any) => renderActions(row),
      },
    ],
    [renderPercentageChange, renderCommission, renderBadge, renderActions]
  );

  const handleDeleteAd = useCallback(async () => {
    if (!selectedObj) return;
    setIsLoading(true);
    let result = await AdService.deleteById(token, selectedObj?._id);
    if (result.error) {
      setErrorMessage(result.error);
      setIsLoading(false);
      return;
    }

    setIsLoading(false);
    setSelectedObj(null);
    setShowDeleteConfirm(false);
    refetch();
  }, [selectedObj, setIsLoading, setErrorMessage, refetch, token]);

  return (
    <>
      <Confirm
        show={showDeleteConfirm}
        onNo={() => {
          setSelectedObj(null);
          setShowDeleteConfirm(false);
        }}
        onYes={handleDeleteAd}
        title="Are you sure?"
        body="Are you sure you want to delete this listing? This action is irreversible."
      />
      <AdDetailsPopup
        show={showDetailsPopup}
        onHide={() => {
          setSelectedObj(null);
          setShowDetailsPopup(false);
        }}
        data={selectedObj}
      />
      <GetVerifiedFirst
        show={showGetVerifiedFirst}
        onHide={() => setShowGetVerifiedFirst(false)}
        reason={gateReason}
        onSuccess={() => {
          setShowGetVerifiedFirst(false);
          navigate("/dashboard/profile");
        }}
        onCompleteProfile={() => {
          setShowGetVerifiedFirst(false);
          navigate("/dashboard/profile");
        }}
        onSetUpTrustap={() => {
          setShowGetVerifiedFirst(false);
          navigate("/dashboard/trustap-account");
        }}
      />
      <div className="bg-pink p-4">
        <div className="row">
          <div className="col-12">
            <div className="chartCard py-4">
              <div className="tableCardHeader px-4">
                <div className="tableCardHeader__titleBlock">
                  <h2 className="tableCardHeader__title m-0">Listing</h2>
                  <p className="tableCardHeader__subtitle m-0">
                    {isDone
                      ? `${count} ${count === 1 ? "listing" : "listings"} total`
                      : "Loading your listings…"}
                  </p>
                </div>
                <div className="tableCardHeader__actions customTitle">
                  <AdStatusDropdown value={status} onChange={setStatus} />
                  <AdSortingDropdown value={sorting} onChange={setSorting} />
                  <TableSearchInput
                    value={keyword}
                    onChange={setKeyword}
                    onEnter={() => setAppliedKeyword(keyword)}
                  />
                  <div className="tableCardHeader__cta">
                    <button
                      type="button"
                      className="placeBidModal__submit"
                      onClick={handleCreateClick}
                    >
                      <i className="bi bi-plus-lg" aria-hidden="true" />
                      <span>Create listing</span>
                    </button>
                  </div>
                </div>
              </div>
              <Table
                rowsPerPage={rowsPerPage}
                setRowsPerPage={setRowsPerPage}
                page={page}
                setPage={setPage}
                columns={columns}
                rows={list}
                isDone={isDone}
                count={count}
                mobileCard={renderMobileCard}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default List;
